// @ts-strict-ignore
import React, { useContext, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Box } from '@usealloy/component-library';
import capitalize from 'lodash/capitalize';

import ApplicationEvaluationHeader from '~/application-review/EvaluationHeader';
import { PortfolioEvaluationContent } from '~/application-review/PortfolioEvaluationView/PortfolioEvaluationContent';
import EntityHeaderInfo from '~/components/EntityHeader';
import { UserContext } from '~/dashboard/context';
import { SPA_PREFIX } from '~/dashboard/constants';
import { generateAudit } from '~/dashboard/helpers';
import LoadingView from '~/dashboard/views/LoadingView';
import { routes } from '~/helpers/routes';

import { useEntity, useEntityPortfolioEvaluation, useEntityPortfolioEvaluationTags } from './hooks';

const Wrapper = styled.div`
  margin-top: calc(var(--cl-space-5) * -1);
`;

const ContentWrapper = styled.div`
  padding: var(--cl-space-5) var(--cl-space-6) var(--cl-space-6);
`;

export const EntityHeader = styled.header`
  padding: var(--cl-space-6) var(--cl-space-6) 0;
`;

export const PortfolioEvaluationView: React.FC = () => {
  const { entityToken, portfolioEvaluationToken } = useParams();
  const {
    permissions,
    typhonDomain,
    updateBreadcrumbs,
    setIsInformationalView,
  } = useContext(UserContext);

  const {
    data: portfolioEvaluation,
    isLoading: isLoadingPortfolioEvaluation,
  } = useEntityPortfolioEvaluation({
    entityToken,
    portfolioEvaluationToken,
  });
  const {
    data: tags,
    isLoading: isLoadingPortfolioEvaluationTags,
  } = useEntityPortfolioEvaluationTags({
    entityToken,
    portfolioEvaluationToken,
  });
  const {
    data: entity,
    isLoading: isLoadingEntity,
  } = useEntity({
    entityToken: portfolioEvaluation?.entities?.[0]?.entity_token,
  });
  const isLoading = (
    isLoadingPortfolioEvaluation
    || isLoadingPortfolioEvaluationTags
    || isLoadingEntity
  );

  const isInformational = portfolioEvaluation?.decision_type === 'informational';

  const evaluationData = useMemo(() => ({
    portfolio_evaluation_token: portfolioEvaluationToken,
    evaluation_type: 'portfolio',
    status: 'portfolio',
    application_url: `${SPA_PREFIX}/workflows/${portfolioEvaluation?.workflow_token}/`,
    application_name: portfolioEvaluation?.workflow_name,
    public_version: portfolioEvaluation?.workflow_version,
    outcome: portfolioEvaluation?.entities?.[0].portfolio_outcome,
    timestamp: portfolioEvaluation?.created_at,
  }), [portfolioEvaluation, entity]);

  useEffect(() => {
    if (!entity) {
      return;
    }

    const entityURL = routes.entities.detail(entity.entity_token);
    const portfolioEvaluationURL = routes.v3.dashboard.portfolioEvaluations
      .portfolioEvaluation(portfolioEvaluationToken);
    const thisURL = routes.v3.dashboard.entities.portfolioEvaluation({
      entityToken: entity.entity_token,
      portfolioEvaluationToken,
    });

    const breadcrumbs = [
      {
        href: routes.entities.list,
        text: 'Entities',
      },
      {
        href: entityURL,
        text: entity.entity_name,
      },
      {
        href: routes.v3.dashboard.portfolioEvaluations.list,
        text: 'Portfolio Evaluations',
      },
      {
        href: portfolioEvaluationURL,
        text: portfolioEvaluationToken,
      },
    ];

    updateBreadcrumbs(breadcrumbs);

    generateAudit(typhonDomain, 'Viewed Entity Event (Portfolio Evaluation) page.', {
      entity: {
        token: entity.entity_token,
        link: entityURL,
      },
      portfolio_evaluation: {
        token: portfolioEvaluationToken,
        link: portfolioEvaluationURL,
      },
      page_link: thisURL,
    });
  }, [entity]);

  useEffect(() => {
    setIsInformationalView(isInformational);

    return () => {
      setIsInformationalView(false);
    };
  }, [isInformational]);

  if (isLoading) {
    return (
      <Box direction="column" alignItems="center" justifyContent="center" style={{ padding: 'var(--cl-space-6) 0' }}>
        <LoadingView />
      </Box>
    );
  }

  return (
    <Wrapper>
      <EntityHeader>
        <EntityHeaderInfo
          icon={capitalize(entity.type) as 'Business' | 'Group' | 'Person'}
          name={entity.entity_name}
          token={entity.entity_token}
        />
      </EntityHeader>
      <ApplicationEvaluationHeader
        apiUrl={typhonDomain}
        doPreventReload
        evaluation={evaluationData}
        isSwitchWorkflowEnabled={false}
        permissions={permissions}
        isInformational={isInformational}
        isWorkflowVersionActive={portfolioEvaluation.is_workflow_version_active}
      />
      <ContentWrapper>
        <PortfolioEvaluationContent
          entity={entity}
          outcome={evaluationData.outcome}
          portfolioEvaluationToken={portfolioEvaluationToken}
          tags={tags}
          workflowToken={portfolioEvaluation.workflow_token}
          workflowVersion={portfolioEvaluation.workflow_version}
        />
      </ContentWrapper>
    </Wrapper>
  );
};
